import { FUEHRERSCHEIN_KFZ, KFZ_KLASSEN, klassenSchreiben } from "../data/ausweisarten";
import type { MapperErgebnis, UebergangenerWert } from "./visitenkarteZuFormState";
import { uid, type ContactRef, type HouseholdItem } from "../components/relationsInline/types";

/**
 * Übersetzt die Befunde von Führerschein, Geburts-, Sterbe- und Heiratsurkunde in
 * die Feldschlüssel der Personen-Anlage (`NewPrivateContactForm`).
 *
 * Muster wie `personalausweisZuFormState`: Binding-Keys der Maske, katalog-
 * gebundene Werte nur bei Treffer, alles andere als `uebergangen` sichtbar.
 *
 * Die Dokumentart selbst (`idCardType`) setzt der Mapper NICHT: Sie hängt an
 * der Scan-Art, nicht an einem einzelnen Feld, und der Dialog prüft je Feld,
 * ob sein Ziel schon belegt ist — ein mitgeschriebenes `idCardType` wäre in
 * der Maske immer belegt („Personalausweis" ist Vorgabe) und sperrte so jede
 * Angabe. Welcher Dokumenteintrag gefüllt wird, entscheidet
 * `dokumentScanVerteilen`.
 */

type Verworfen = { verworfen?: Array<{ feld: string; wert: string }> };

export type FuehrerscheinBefund = Partial<{
  nachname: string;
  vornamen: string;
  geburtsdatum: string;
  geburtsort: string;
  ausstellungsdatum: string;
  gueltigBis: string;
  behoerde: string;
  fuehrerscheinnummer: string;
  klassen: string[];
}> &
  Verworfen;

export type GeburtsurkundeBefund = Partial<{
  nachname: string;
  vornamen: string;
  geburtsname: string;
  geburtsdatum: string;
  geburtszeit: string;
  geburtsort: string;
  registernummer: string;
  standesamt: string;
  ausstellungsdatum: string;
}> &
  Verworfen;

export type SterbeurkundeBefund = Partial<{
  nachname: string;
  vornamen: string;
  geburtsname: string;
  geburtsdatum: string;
  geburtsort: string;
  sterbedatum: string;
  sterbeort: string;
  registernummer: string;
  standesamt: string;
  ausstellungsdatum: string;
}> &
  Verworfen;

export type HeiratsurkundeBefund = Partial<{
  ehegatte1Nachname: string;
  ehegatte1Vornamen: string;
  ehegatte1Geburtsname: string;
  ehegatte1Geburtsdatum: string;
  ehegatte1Geburtsort: string;
  ehegatte1Geschlecht: string;
  ehegatte2Nachname: string;
  ehegatte2Vornamen: string;
  ehegatte2Geburtsname: string;
  ehegatte2Geburtsdatum: string;
  ehegatte2Geburtsort: string;
  ehegatte2Geschlecht: string;
  ehename: string;
  eheschliessungsdatum: string;
  eheschliessungsort: string;
  registernummer: string;
  standesamt: string;
  ausstellungsdatum: string;
}> &
  Verworfen;

const gesetzt = (wert: unknown): wert is string => typeof wert === "string" && wert.trim() !== "";

/** Die Personenangaben, die alle drei Dokumente teilen. */
const person = (
  b: { nachname?: string; vornamen?: string; geburtsname?: string; geburtsdatum?: string; geburtsort?: string },
  werte: Record<string, string>,
) => {
  if (gesetzt(b.nachname)) werte.lastname = b.nachname;
  if (gesetzt(b.vornamen)) werte.firstname = b.vornamen;
  if (gesetzt(b.geburtsname)) werte.birthname = b.geburtsname;
  if (gesetzt(b.geburtsdatum)) werte.dateofbirth = b.geburtsdatum;
  if (gesetzt(b.geburtsort)) werte.placeofbirth = b.geburtsort;
};

/** Führerschein → Personen-Maske und Dokumenteintrag. */
export const fuehrerscheinZuPrivatFormState = (befund: FuehrerscheinBefund): MapperErgebnis => {
  const werte: Record<string, string> = {};
  const uebergangen: UebergangenerWert[] = [];
  person(befund, werte);

  if (gesetzt(befund.fuehrerscheinnummer)) werte.idCardNumber = befund.fuehrerscheinnummer;
  if (gesetzt(befund.ausstellungsdatum)) werte.idCardIssuedAt = befund.ausstellungsdatum;
  if (gesetzt(befund.gueltigBis)) werte.idCardValidUntil = befund.gueltigBis;
  if (gesetzt(befund.behoerde)) werte.idCardAuthority = befund.behoerde;

  // Die Klassen eines Kartenführerscheins sind Kraftfahrzeug-Klassen — sie
  // bestimmen damit auch die Unterart. Was der Katalog der Maske nicht kennt
  // (etwa „B96"), wird gezeigt statt still verworfen.
  const klassen = Array.isArray(befund.klassen) ? befund.klassen.filter(gesetzt) : [];
  if (klassen.length) {
    const bekannt = klassen.filter((k) => KFZ_KLASSEN.includes(k));
    const fremd = klassen.filter((k) => !KFZ_KLASSEN.includes(k));
    if (bekannt.length) {
      werte.idCardClasses = klassenSchreiben(bekannt, KFZ_KLASSEN);
      werte.idCardSubtype = FUEHRERSCHEIN_KFZ;
    }
    for (const k of fremd) {
      uebergangen.push({ feld: "Klasse", wert: k, grund: "steht nicht im Klassen-Katalog der Maske" });
    }
  }

  return { werte, uebergangen };
};

/** Geburtsurkunde → Personen-Maske und Dokumenteintrag. */
export const geburtsurkundeZuPrivatFormState = (befund: GeburtsurkundeBefund): MapperErgebnis => {
  const werte: Record<string, string> = {};
  person(befund, werte);
  if (gesetzt(befund.geburtszeit)) werte.birthTime = befund.geburtszeit;
  if (gesetzt(befund.registernummer)) werte.idCardNumber = befund.registernummer;
  if (gesetzt(befund.standesamt)) werte.idCardAuthority = befund.standesamt;
  if (gesetzt(befund.ausstellungsdatum)) werte.idCardIssuedAt = befund.ausstellungsdatum;
  return { werte, uebergangen: [] };
};

/**
 * Sterbeurkunde → Personen-Maske und Dokumenteintrag.
 *
 * Das Sterbedatum geht nach `deceasedAt`. Den Schalter „Verstorben" setzt
 * `dokumentScanVerteilen` dazu — hier mitgeschrieben, würde ein bereits auf
 * „Ja" stehender Schalter das Datum als „schon belegt" sperren.
 */
export const sterbeurkundeZuPrivatFormState = (befund: SterbeurkundeBefund): MapperErgebnis => {
  const werte: Record<string, string> = {};
  const uebergangen: UebergangenerWert[] = [];
  person(befund, werte);
  if (gesetzt(befund.sterbedatum)) werte.deceasedAt = befund.sterbedatum;
  if (gesetzt(befund.registernummer)) werte.idCardNumber = befund.registernummer;
  if (gesetzt(befund.standesamt)) werte.idCardAuthority = befund.standesamt;
  if (gesetzt(befund.ausstellungsdatum)) werte.idCardIssuedAt = befund.ausstellungsdatum;
  if (gesetzt(befund.sterbeort)) {
    uebergangen.push({ feld: "Sterbeort", wert: befund.sterbeort, grund: "kein Feld in der Kontaktmaske" });
  }
  return { werte, uebergangen };
};

// ── Heiratsurkunde (U3) ─────────────────────────────────────────────────────

/** Familienstand, den eine Heiratsurkunde belegt. */
export const FAMILIENSTAND_VERHEIRATET = "Verheiratet";

type Ehegatte = 1 | 2;

const falten = (s: unknown): string =>
  String(s ?? "")
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]/g, "");

const gatte = (b: HeiratsurkundeBefund, nr: Ehegatte) => ({
  nachname: b[`ehegatte${nr}Nachname`],
  vornamen: b[`ehegatte${nr}Vornamen`],
  geburtsname: b[`ehegatte${nr}Geburtsname`],
  geburtsdatum: b[`ehegatte${nr}Geburtsdatum`],
  geburtsort: b[`ehegatte${nr}Geburtsort`],
  geschlecht: b[`ehegatte${nr}Geschlecht`],
});

/**
 * Welcher der beiden Ehegatten ist der Kontakt, der gerade angelegt wird?
 *
 * Abgeglichen mit dem, was in der Maske schon steht: Geburtsdatum (stärkstes
 * Indiz), Vorname, Name und die Anrede gegen das gedruckte Geschlecht. Ohne
 * jeden Anhaltspunkt ist es Ehegatte 1 — `sicher: false`, der Dialog fragt dann
 * sichtbar nach.
 */
export function eigenerEhegatte(
  befund: HeiratsurkundeBefund,
  vorhanden: Record<string, string | undefined> = {},
): { nr: Ehegatte; sicher: boolean } {
  const punkte = (nr: Ehegatte): number => {
    const g = gatte(befund, nr);
    let p = 0;
    if (gesetzt(vorhanden.dateofbirth) && g.geburtsdatum === vorhanden.dateofbirth) p += 4;
    const vorname = falten(String(vorhanden.firstname ?? "").split(/\s+/)[0]);
    if (vorname && falten(g.vornamen).startsWith(vorname)) p += 2;
    const name = falten(vorhanden.lastname);
    if (name && [g.nachname, g.geburtsname, befund.ehename].some((n) => falten(n) === name)) p += 1;
    const anrede = String(vorhanden.anrede ?? "").trim();
    const geschlecht = String(g.geschlecht ?? "");
    if ((anrede === "Herr" && geschlecht === "männlich") || (anrede === "Frau" && geschlecht === "weiblich")) p += 1;
    return p;
  };
  const p1 = punkte(1);
  const p2 = punkte(2);
  if (p1 === p2) return { nr: 1, sicher: false };
  return { nr: p1 > p2 ? 1 : 2, sicher: true };
}

export type HeiratKontext = {
  /** Welcher Ehegatte der Kontakt ist (`eigenerEhegatte`). */
  selbst: Ehegatte;
  /** Der Ehename der Urkunde — bestimmt, was Name und was Geburtsname ist. */
  ehename?: string;
};

/**
 * Heiratsurkunde → Personen-Maske und Dokumenteintrag (U3).
 *
 * - Datum der Eheschließung → „Hochzeitstag (standesamtlich)".
 * - Führt das Paar einen Ehenamen, ist er der Name; der Name vor der Ehe wird
 *   zum Geburtsnamen, wenn er davon abweicht.
 * - Die Angaben des ANDEREN Ehegatten haben hier kein Feld — er wird im Dialog
 *   als Beziehung (Haushalt) vorgeschlagen (`heiratEhepartner`).
 * - Den Familienstand setzt der Dialog, nicht ein einzelnes Feld: Er hängt an
 *   der Urkunde als Ganzes.
 */
export const heiratsurkundeZuPrivatFormState = (
  befund: HeiratsurkundeBefund,
  kontext: HeiratKontext = { selbst: 1 },
): MapperErgebnis => {
  const werte: Record<string, string> = {};
  const uebergangen: UebergangenerWert[] = [];
  const selbst = kontext.selbst;
  const andere: Ehegatte = selbst === 1 ? 2 : 1;
  const ehename = gesetzt(kontext.ehename) ? kontext.ehename : undefined;

  const vornamen = befund[`ehegatte${selbst}Vornamen`];
  const nachname = befund[`ehegatte${selbst}Nachname`];
  const geburtsname = befund[`ehegatte${selbst}Geburtsname`];
  if (gesetzt(vornamen)) werte.firstname = vornamen;
  if (gesetzt(befund.ehename)) werte.lastname = befund.ehename;
  if (gesetzt(nachname)) {
    if (!ehename) werte.lastname = nachname;
    else if (falten(nachname) !== falten(ehename)) werte.birthname = nachname;
  }
  if (gesetzt(geburtsname) && (!ehename || falten(geburtsname) !== falten(ehename))) werte.birthname = geburtsname;
  const gd = befund[`ehegatte${selbst}Geburtsdatum`];
  const go = befund[`ehegatte${selbst}Geburtsort`];
  if (gesetzt(gd)) werte.dateofbirth = gd;
  if (gesetzt(go)) werte.placeofbirth = go;

  if (gesetzt(befund.eheschliessungsdatum)) werte.weddingDateCivil = befund.eheschliessungsdatum;
  if (gesetzt(befund.registernummer)) werte.idCardNumber = befund.registernummer;
  if (gesetzt(befund.standesamt)) werte.idCardAuthority = befund.standesamt;
  if (gesetzt(befund.ausstellungsdatum)) werte.idCardIssuedAt = befund.ausstellungsdatum;
  if (gesetzt(befund.eheschliessungsort)) {
    uebergangen.push({ feld: "Ort der Eheschließung", wert: befund.eheschliessungsort, grund: "kein Feld in der Kontaktmaske" });
  }

  for (const feld of ["Nachname", "Vornamen", "Geburtsname", "Geburtsdatum", "Geburtsort", "Geschlecht"] as const) {
    const wert = befund[`ehegatte${andere}${feld}`];
    if (gesetzt(wert)) {
      uebergangen.push({
        feld: `Ehegatte ${andere}: ${feld}`,
        wert,
        grund: "Ehepartner — wird als Beziehung im Haushalt vorgeschlagen",
      });
    }
  }
  return { werte, uebergangen };
};

/**
 * Der andere Ehegatte als Beziehungsvorschlag (Haushalt) — U3.
 *
 * Nur Vorbelegung der Maske: eine `manual`-Referenz, angelegt wird sie wie
 * jede Haushaltszeile erst beim Speichern. Die Beziehung („Ehemann" /
 * „Ehefrau", aus Sicht des Kontakts: was der ANDERE für ihn ist) folgt dem
 * gedruckten Geschlecht; fehlt es, dem Gegengeschlecht der Anrede. Ohne beides
 * bleibt sie leer — der Anwender wählt sie selbst, statt dass geraten wird.
 */
export function heiratEhepartner(
  befund: HeiratsurkundeBefund,
  kontext: HeiratKontext & { anrede?: string },
): { ref: ContactRef; relationLabel: string } | null {
  const andere: Ehegatte = kontext.selbst === 1 ? 2 : 1;
  const g = gatte(befund, andere);
  const nachname = gesetzt(befund.ehename) ? befund.ehename : g.nachname;
  if (!gesetzt(nachname) && !gesetzt(g.vornamen)) return null;
  const ref: ContactRef = {
    manual: true,
    type: "Person",
    ...(gesetzt(g.vornamen) ? { firstName: g.vornamen } : {}),
    ...(gesetzt(nachname) ? { lastName: nachname } : {}),
    ...(gesetzt(g.geburtsdatum) ? { birthDate: g.geburtsdatum } : {}),
  };
  let label = "";
  if (g.geschlecht === "weiblich") label = "Ehefrau";
  else if (g.geschlecht === "männlich") label = "Ehemann";
  else if (kontext.anrede === "Herr") label = "Ehefrau";
  else if (kontext.anrede === "Frau") label = "Ehemann";
  if (label === "Ehefrau") ref.gender = "Weiblich";
  if (label === "Ehemann") ref.gender = "Männlich";
  return { ref, relationLabel: label };
}

const EHE_LABELS = new Set(["Ehemann", "Ehefrau", "Ehepartner", "Ehepartner(in)"]);

/**
 * Den Ehepartner der Urkunde im Haushalt vorbelegen (U3).
 *
 * Nichts wird überschrieben: Steht dort schon ein Ehepartner oder dieselbe
 * Person, bleibt die Liste, wie sie ist. Ein angefangener leerer Eintrag wird
 * gefüllt, sonst angehängt. Liefert dieselbe Liste, wenn es nichts zu tun gibt.
 */
export function ehepartnerInHaushalt(
  items: HouseholdItem[],
  partner: { ref: ContactRef; relationLabel: string },
): HouseholdItem[] {
  const name = (r?: ContactRef | null) => falten(`${r?.firstName ?? ""}${r?.lastName ?? ""}`);
  const schonDa = items.some(
    (it) => EHE_LABELS.has(it.relationLabel) || (!!it.ref && name(it.ref) === name(partner.ref)),
  );
  if (schonDa) return items;
  const leer = items.findIndex((it) => !it.ref);
  const eintrag = (id: string): HouseholdItem => ({
    id,
    ref: partner.ref,
    relationLabel: partner.relationLabel,
    sameHousehold: true,
  });
  return leer >= 0
    ? items.map((it, i) => (i === leer ? { ...it, ...eintrag(it.id) } : it))
    : [...items, eintrag(uid())];
}

/** Anzeigewert eines Befundfelds — die Klassenliste als „B, BE". */
export const urkundeAnzeigewert = (befund: Record<string, unknown>, feld: string): string => {
  const wert = befund[feld];
  if (Array.isArray(wert)) return wert.filter(gesetzt).join(", ");
  return typeof wert === "string" ? wert : "";
};

export const FUEHRERSCHEIN_LABELS: Record<string, string> = {
  nachname: "Name",
  vornamen: "Vornamen",
  geburtsdatum: "Geburtsdatum",
  geburtsort: "Geburtsort",
  fuehrerscheinnummer: "Führerscheinnummer",
  klassen: "Klassen",
  ausstellungsdatum: "Ausgestellt am",
  gueltigBis: "Gültig bis",
  behoerde: "Behörde",
};
export const FUEHRERSCHEIN_REIHENFOLGE = Object.keys(FUEHRERSCHEIN_LABELS);

export const GEBURTSURKUNDE_LABELS: Record<string, string> = {
  nachname: "Name",
  vornamen: "Vornamen",
  geburtsname: "Geburtsname",
  geburtsdatum: "Geburtsdatum",
  geburtszeit: "Geburtszeit",
  geburtsort: "Geburtsort",
  registernummer: "Registernummer",
  standesamt: "Standesamt",
  ausstellungsdatum: "Ausgestellt am",
};
export const GEBURTSURKUNDE_REIHENFOLGE = Object.keys(GEBURTSURKUNDE_LABELS);

export const STERBEURKUNDE_LABELS: Record<string, string> = {
  nachname: "Name",
  vornamen: "Vornamen",
  geburtsname: "Geburtsname",
  geburtsdatum: "Geburtsdatum",
  geburtsort: "Geburtsort",
  sterbedatum: "Sterbedatum",
  sterbeort: "Sterbeort",
  registernummer: "Registernummer",
  standesamt: "Standesamt",
  ausstellungsdatum: "Ausgestellt am",
};
export const STERBEURKUNDE_REIHENFOLGE = Object.keys(STERBEURKUNDE_LABELS);

export const HEIRATSURKUNDE_LABELS: Record<string, string> = {
  ehegatte1Vornamen: "Ehegatte 1: Vornamen",
  ehegatte1Nachname: "Ehegatte 1: Name vor der Ehe",
  ehegatte1Geburtsname: "Ehegatte 1: Geburtsname",
  ehegatte1Geburtsdatum: "Ehegatte 1: Geburtsdatum",
  ehegatte1Geburtsort: "Ehegatte 1: Geburtsort",
  ehegatte2Vornamen: "Ehegatte 2: Vornamen",
  ehegatte2Nachname: "Ehegatte 2: Name vor der Ehe",
  ehegatte2Geburtsname: "Ehegatte 2: Geburtsname",
  ehegatte2Geburtsdatum: "Ehegatte 2: Geburtsdatum",
  ehegatte2Geburtsort: "Ehegatte 2: Geburtsort",
  ehename: "Ehename",
  eheschliessungsdatum: "Standesamtliche Trauung",
  eheschliessungsort: "Ort der Eheschließung",
  registernummer: "Registernummer",
  standesamt: "Standesamt",
  ausstellungsdatum: "Ausgestellt am",
};
export const HEIRATSURKUNDE_REIHENFOLGE = Object.keys(HEIRATSURKUNDE_LABELS);

/**
 * Führt Vorder- und Rückseite zusammen (beim Führerschein stehen die Klassen
 * hinten). Die Vorderseite gewinnt; die Klassen werden vereinigt, die
 * verworfenen Werte beider Seiten gesammelt.
 */
export const urkundeSeitenZusammenfuehren = (
  vorne: Record<string, unknown>,
  hinten: Record<string, unknown> = {},
): { felder: Record<string, unknown>; konflikte: Array<{ feld: string; vorne: string; hinten: string }> } => {
  const felder: Record<string, unknown> = { ...vorne };
  const konflikte: Array<{ feld: string; vorne: string; hinten: string }> = [];
  for (const [feld, h] of Object.entries(hinten)) {
    const v = vorne[feld];
    if (Array.isArray(h)) {
      const alle = [...(Array.isArray(v) ? v : []), ...h];
      felder[feld] = feld === "verworfen" ? alle : Array.from(new Set(alle as string[]));
      continue;
    }
    if (!gesetzt(h)) continue;
    if (!gesetzt(v)) felder[feld] = h;
    else if (v.toLowerCase() !== h.toLowerCase()) konflikte.push({ feld, vorne: v, hinten: h });
  }
  return { felder, konflikte };
};
